// @ts-strict-ignore
import { FormInstance, useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import { UploadFile } from 'antd/lib/upload/interface';
import * as React from 'react';
import {
    CreateIntegrationRequestDataType,
    CrowdinIntegrationRequestDataType,
    DatadogDatacenter,
    HuaweiIntegrationRequestDataType,
    LokaliseIntegrationRequestDataType,
    OpsgenieIntegrationRequestDataType,
    PagerDutyIntegrationRequestDataType,
    RootlyIntegrationRequestDataType,
    IncidentIoIntegrationRequestDataType,
    SamsungIntegrationRequestDataType,
} from '../actions/integrations';
import {
    FieldType,
    FormFieldData,
    formValidAndRequiredFieldsNotEmpty,
    integrationModalContentUsingFormInstance,
} from '../helpers/formHelpers';
import { isRequiredRule } from '../helpers/integrationFormHelpers';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';
import { IntegrationType, RunwayResponse } from '../types';
import { AddIntegrationModalProps } from './setup/useAddIntegrationModal';
import UploadFileComponent from './UploadFileComponent';
import { allCases } from '../typescriptHelpers';
import Alert from 'antd/lib/alert';
import Tooltip from 'antd/lib/tooltip';
import Link from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';

interface ConnectIntegrationModalProps extends AddIntegrationModalProps {
    appId: string;
}

const ConnectIntegrationModal: React.FC<ConnectIntegrationModalProps> = (props: ConnectIntegrationModalProps) => {
    const [fileList, setFileList] = React.useState<UploadFile<unknown>[]>([]);
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();

    const onCancel = () => {
        props.onCancel();
    };

    const onFieldsChange = (integrationId: IntegrationType, form: FormInstance) => {
        const requiredFieldsForIntegrationId = connectDataForIntegrationType(integrationId)
            .filter((field) => field.rules.find(isRequiredRule))
            .map((field) => field.name);
        const formValid = formValidAndRequiredFieldsNotEmpty(form, requiredFieldsForIntegrationId);
        setFormIsValid(formValid);
    };

    const onFinish = (_, integrationType: IntegrationType, form: FormInstance) => {
        form.validateFields().then((values) => {
            const initialData = validateFormValuesForIntegrationType(integrationType, values);

            let data = initialData;

            if (needsFileUpload) {
                data = {
                    ...initialData,
                    s3Key: s3KeyFromFileUpload(fileList),
                };
            }

            props.addIntegration(integrationType, data);
        });
    };

    const onUpdateFileList = (integrationId: IntegrationType, fileList: UploadFile<unknown>[]) => {
        setFileList(fileList);

        const requiredFieldsForIntegrationId = connectDataForIntegrationType(integrationId)
            .filter((field) => field.rules.find(isRequiredRule))
            .map((field) => field.name);

        const formValid = formValidAndRequiredFieldsNotEmpty(form, requiredFieldsForIntegrationId);

        setFormIsValid(formValid);
    };

    const isValid = () => {
        if (!needsFileUpload) {
            return formIsValid;
        } else {
            const s3Key = s3KeyFromFileUpload(fileList);
            return s3Key && formIsValid;
        }
    };

    const fields = props.integrationType ? connectDataForIntegrationType(props.integrationType) : null;
    const isLoading = props.isLoading;
    const title = props.integrationType ? displayNameForIntegrationType(props.integrationType) : null;
    const needsFileUpload = requiresFileUpload(props.integrationType);
    // TODO: Hacky way to get connect button to be enabled for Build Distro
    const okButtonEnabled = isValid() || props.integrationType === IntegrationType.RunwayBuildDistroBeta;
    const warningMessage = warningMessageForIntegrationType(props.integrationType);

    return (
        <Modal
            title={`Connect ${title}`}
            onCancel={onCancel}
            width={600}
            confirmLoading={isLoading}
            open={props.open}
            okButtonProps={{ disabled: !okButtonEnabled }}
            onOk={() => form.submit()}
            okText="Connect"
            afterClose={() => {
                form.resetFields();
                setFileList([]);
                setFormIsValid(false);
            }}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch', gap: 16 }}>
                {props.integrationType === IntegrationType.CustomAnalytics && (
                    <Text style={{ textAlign: 'justify' }}>
                        Configure a custom observability & analytics integration to send pre-computed metrics from your
                        team’s internal system to your app in Runway. Once configured, data can be pushed to the
                        integration in Runway via the public API. For more information, visit our
                        <Text> </Text>
                        <Link
                            href="https://docs.runway.team/integrations/observability-and-analytics/custom-analytics"
                            target="_blank"
                        >
                            documentation
                        </Link>
                        .
                    </Text>
                )}
                {props.integrationType &&
                    fields &&
                    integrationModalContentUsingFormInstance(
                        form,
                        'vertical',
                        props.integrationType,
                        null,
                        fields,
                        onFieldsChange,
                        onFinish,
                    )}
                {props.integrationType === IntegrationType.IncidentIoScheduling && <IncidentIoApiKeyPermissionsInfo />}
                {needsFileUpload && props.integrationType && (
                    <UploadFileComponent
                        orgId={props.orgId}
                        appId={props.appId}
                        integrationId={props.integrationType}
                        fileList={fileList}
                        onUpdateFileList={onUpdateFileList}
                    />
                )}
                {warningMessage && <Alert showIcon message={warningMessage} type={'warning'} />}
            </div>
        </Modal>
    );
};

export default ConnectIntegrationModal;

export const connectDataForIntegrationType: (integrationType: IntegrationType) => FormFieldData[] = (
    integrationType: IntegrationType,
) => {
    switch (integrationType) {
        case IntegrationType.Codemagic:
            return [
                {
                    label: 'Personal Access Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'Generate a Personal Access Token in Codemagic by going to Teams > Personal Account > Integrations > Codemagic API > Show',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Bitrise:
            return [
                {
                    label: 'Personal Access Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'Generate a Personal Access Token in Bitrise by going to Profile Settings > Security > Generate new ',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Buildkite:
            return [
                {
                    label: 'Access Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'Generate an Access Token in Buildkite by going to Personal Settings > API Access Tokens > New API Access Token ',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Bugsnag:
            return [
                {
                    label: 'Personal Auth Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'Generate a Personal Auth Token in Bugsnag by going to Settings > My account > Personal auth tokens > Generate new ',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Sentry:
            return [
                {
                    label: 'Auth Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an Auth Token in Sentry by going User Settings > API > Auth Tokens ',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.TestRail:
            return [
                {
                    label: 'Base URL',
                    name: 'url',
                    fieldType: FieldType.Input,
                    placeholder: '',
                    helpMessage: "Base URL of your TestRail instance (including 'https://')",
                    rules: [{ required: true }],
                },
                {
                    label: 'API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an API Token in TestRail by going to My Settings > API Keys > Add Key ',
                    rules: [{ required: true }],
                },
                {
                    label: 'Username',
                    name: 'username',
                    fieldType: FieldType.Input,
                    placeholder: '',
                    helpMessage: 'Email address of account associated with API Token',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.BrowserStack:
            return [
                {
                    label: 'Username',
                    name: 'username',
                    fieldType: FieldType.Input,
                    placeholder: '',
                    helpMessage:
                        'Generate a username and associated API access key in BrowserStack by going to Settings > Product > Service Accounts ',
                    rules: [{ required: true }],
                },
                {
                    label: 'API Access Key',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Qase:
            return [
                {
                    label: 'API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an API Token in Qase by going to https://app.qase.io/user/api/token ',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Shortcut:
            return [
                {
                    label: 'API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'Find your API token in Shortcut by going to https://app.shortcut.com/settings/account/api-tokens',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Pivotal:
            return [
                {
                    label: 'API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Find your API token in Pivotal Tracker by going to pivotaltracker.com/profile',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Jenkins:
            return [
                {
                    label: 'URL',
                    name: 'jenkinsUrl',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '',
                    helpMessage: 'URL of your Jenkins server',
                },
                {
                    label: 'Jenkins Username',
                    name: 'jenkinsUsername',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '',
                    helpMessage: 'Name of your Jenkins user',
                },
                {
                    label: 'API Token',
                    name: 'jenkinsApiToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an API token in Jenkins by going to /user/admin/configure ',
                },
            ];
        case IntegrationType.TestFlight:
        case IntegrationType.Apple:
        case IntegrationType.XcodeCloud:
            return [
                {
                    label: 'Key ID',
                    name: 'keyId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '',
                },
                {
                    label: 'Issuer ID',
                    name: 'issuerId',
                    fieldType: FieldType.Input,
                    placeholder: '',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.GoogleBeta:
            return [
                {
                    label: 'Package name',
                    name: 'packageName',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Developer account ID',
                    name: 'developerId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '123345',
                    helpMessage:
                        'You can find your developer account ID under Account Details in the Google Play Console',
                },
            ];
        case IntegrationType.FirebaseBeta:
            return [];
        case IntegrationType.GoogleAnalytics:
            return [
                {
                    label: 'Property ID',
                    name: 'propertyId',
                    placeholder: '123456789',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage: 'You can find your Google Analytics property ID in the Google Analytics Admin page',
                },
            ];
        case IntegrationType.AndroidVitals:
            return [];
        case IntegrationType.ApplePowerPerformanceMetrics:
            return [];
        case IntegrationType.Google:
            return [
                {
                    label: 'Package name',
                    name: 'packageName',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Developer account ID',
                    name: 'developerId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '123345',
                    helpMessage:
                        'You can find your developer account ID under Account Details in the Google Play Console',
                },
            ];
        case IntegrationType.Amazon:
            return [
                {
                    label: 'Client ID',
                    name: 'clientId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Client Secret',
                    name: 'clientSecret',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
            ];
        case IntegrationType.CircleCI:
            return [
                {
                    label: 'Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'Generate a Personal API Token in CircleCI by going to User Settings > Personal API Tokens > Create New Token ',
                },
            ];
        case IntegrationType.TravisCI:
            return [
                {
                    label: 'Host URL',
                    name: 'url',
                    data: ['travis-ci.com', 'travis-ci.org'].map((u) => {
                        return {
                            value: u,
                            displayValue: u,
                        };
                    }), // hardcoded for now, may need to open up for self-hosted
                    rules: [{ required: true }],
                    fieldType: FieldType.Select,
                    placeholder: '',
                    helpMessage: 'Host URL of your TravisCI server',
                },
                {
                    label: 'API Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an API token in the TravisCI command line client ',
                },
            ];
        case IntegrationType.Firebase:
            return [];
        case IntegrationType.Xray:
            return [
                {
                    label: 'Client ID',
                    name: 'username',
                    fieldType: FieldType.Input,
                    placeholder: '',
                    rules: [{ required: true }],
                },
                {
                    label: 'Client Secret',
                    name: 'password',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.AppCenterCI:
            return [
                {
                    label: 'User API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate a User API Token by going to Account Settings > User API Tokens',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.AppCenterBeta:
            return [
                {
                    label: 'User API Token',
                    name: 'accessToken',
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate a User API Token by going to Account Settings > User API Tokens',
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.Amplitude:
            return [
                {
                    label: 'API Key',
                    name: 'apiKey',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        "You can find your Amplitude project's API key by visiting Org Settings > Projects > General",
                },
                {
                    label: 'API Secret',
                    name: 'apiSecret',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        "You can find your Amplitude project's API secret by visiting Org Settings > Projects > General",
                },
                {
                    label: 'Use EU server',
                    name: 'shouldUseEUServer',
                    rules: [],
                    fieldType: FieldType.Switch,
                    switchOnText: 'Yes',
                    switchOffText: 'No',
                    initialValue: false,
                    helpMessage: "Enable if you've configured the Amplitude SDK to send data to the EU server zone",
                },
            ];

        case IntegrationType.Optimizely:
            return [
                {
                    label: 'Personal Access Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Optimizely API key by visiting your Profile > API Access > Personal Access Token',
                },
            ];

        case IntegrationType.LaunchDarkly:
            return [
                {
                    label: 'Personal Access Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your LaunchDarkly API key by visiting Account Settings > Authorization > Access Tokens',
                },
            ];
        case IntegrationType.Statsig:
            return [
                {
                    label: 'API Key',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Statsig API key by visiting Settings > Keys & Environments. Use a Console API Key for console API access.',
                },
            ];

        case IntegrationType.DatadogAnalytics:
        case IntegrationType.DatadogStabilityMonitoring:
            return [
                {
                    label: 'API Key',
                    name: 'apiKey',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'You can find your Datadog API key by visiting Org Settings > Access > API Keys',
                },
                {
                    label: 'Application Key',
                    name: 'applicationKey',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Datadog Application key by visiting Org Settings > Access > Application Keys',
                },
                {
                    label: 'Datadog Site',
                    name: 'datadogDatacenter',
                    data: allCases(DatadogDatacenter).map((u) => {
                        return {
                            value: u,
                            displayValue: u,
                        };
                    }),
                    rules: [{ required: true }],
                    fieldType: FieldType.Select,
                    placeholder: 'Select',
                    helpMessage:
                        'Select the site that matches your Datadog website URL according to https://docs.datadoghq.com/getting_started/site/#access-the-datadog-site',
                },
            ];

        case IntegrationType.NewRelicStabilityMonitoring:
        case IntegrationType.NewRelicAnalytics:
            return [
                {
                    label: 'API Key',
                    name: 'apiKey',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'You can find or create a New Relic API key by visiting [Profile] > API Keys',
                },
            ];

        case IntegrationType.GenericCI:
            // not user-facing for now
            return [];

        case IntegrationType.MixpanelAnalytics:
            return [
                {
                    label: 'Service Account User',
                    name: 'serviceAccountUser',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'You can find more information in Mixpanel under Project Settings > Service Accounts',
                },
                {
                    label: 'Service Account Secret',
                    name: 'serviceAccountSecret',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'You can find more information in Mixpanel under Project Settings > Service Accounts',
                },
                {
                    label: 'Use EU server',
                    name: 'shouldUseEUServer',
                    rules: [],
                    fieldType: FieldType.Switch,
                    switchOnText: 'Yes',
                    switchOffText: 'No',
                    initialValue: false,
                    helpMessage: "Enable if you've configured Mixpanel to send data to the EU server zone",
                },
            ];
        case IntegrationType.RunwayBuildDistroBeta:
            return [];

        case IntegrationType.EmbraceStabilityMonitoring:
            return [
                {
                    label: 'Metrics API token',
                    name: 'apiKey',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Embrace Metrics API token by visiting your Settings > API page in the Embrace dashboard',
                },
            ];

        case IntegrationType.Crowdin:
            return [
                {
                    label: 'API Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Crowdin API token by visiting your Settings > API page in the Crowdin dashboard',
                },
                {
                    label: 'Enterprise Subdomain',
                    name: 'subdomain',
                    rules: [],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'The subdomain in the URL that you use to access your Crowdin Enterprise account. Only required for Crowdin Enterprise customers.',
                },
            ];

        case IntegrationType.Lokalise:
            return [
                {
                    label: 'API Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can find your Lokalise API token by visiting your Profile settings > API tokens page in the Lokalise dashboard',
                },
            ];

        case IntegrationType.PagerDutyScheduling:
            return [
                {
                    label: 'API Key',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can create a PagerDuty API key by visiting Integrations > API Access Keys in the PagerDuty dashboard',
                },
            ];
        case IntegrationType.RootlyScheduling:
            return [
                {
                    label: 'API Key',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can create a Rootly API key by visiting Configuration > API Keys in the Rootly dashboard',
                },
            ];
        case IntegrationType.IncidentIoScheduling:
            return [
                {
                    label: 'API key',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage: 'Generate an API key in incident.io by visiting Settings > API Keys > Add new.',
                },
            ];
        case IntegrationType.HuaweiAppGallery:
            return [
                {
                    label: 'Client ID',
                    name: 'clientId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
                {
                    label: 'Client Secret',
                    name: 'clientSecret',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                },
            ];
        case IntegrationType.Samsung:
            return [
                {
                    label: 'Service Account ID',
                    name: 'serviceAccountId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'You can create a service account by visiting Assistance > API Service in the Samsung Galaxy Store Seller Portal',
                },
            ];
        case IntegrationType.OpsgenieScheduling:
            return [
                {
                    label: 'API Key',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    placeholder: '********',
                    helpMessage:
                        'You can create an Opsgenie API key by visiting Settings > API Key Management in the Opsgenie dashboard',
                },
                {
                    label: '',
                    data: 'Use EU server',
                    name: 'shouldUseEUServer',
                    rules: [],
                    fieldType: FieldType.Checkbox,
                    helpMessage: 'Enable if your team is using the EU instance of Opsgenie',
                },
            ];
        case IntegrationType.DynatraceStabilityMonitoring:
            return [
                {
                    label: 'Access Token',
                    name: 'accessToken',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'Dashboard > Search "Access Tokens" > Generate new access token with "User sessions" permission enabled',
                },
                {
                    label: 'Environment ID',
                    name: 'environmentId',
                    rules: [{ required: true }],
                    fieldType: FieldType.Input,
                    helpMessage:
                        'In the link at the top of the dashboard, e.g. https://<environmentId>.apps.dynatrace.com',
                },
            ];
        case IntegrationType.CustomAnalytics:
            return [
                {
                    label: `Integration name`,
                    helpMessage: 'Assign your custom integration a name for easier data source identification',
                    name: 'displayableName',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.CustomCI:
            return [
                {
                    label: `Integration name`,
                    helpMessage: 'Assign your custom integration a name for easier identification',
                    name: 'displayableName',
                    fieldType: FieldType.Input,
                    rules: [{ required: true }],
                },
            ];
        case IntegrationType.GoogleCalendar:
            return [];
        default:
            return null;
    }
};

export const validateFormValuesForIntegrationType: (
    integrationType: IntegrationType,

    values: any,
) => CreateIntegrationRequestDataType = (integrationType, values) => {
    switch (integrationType) {
        case IntegrationType.Codemagic:
        case IntegrationType.Bitrise:
        case IntegrationType.Buildkite:
        case IntegrationType.Pivotal:
        case IntegrationType.Shortcut:
        case IntegrationType.Bugsnag:
        case IntegrationType.Sentry:
            if (values.accessToken) {
                return {
                    accessToken: values.accessToken,
                };
            } else {
                return null;
            }
        case IntegrationType.TestFlight:
        case IntegrationType.Apple:
        case IntegrationType.XcodeCloud:
            if (values.keyId && values.issuerId) {
                return {
                    keyId: values.keyId,
                    issuerId: values.issuerId,
                };
            } else {
                return null;
            }
        case IntegrationType.Jenkins:
            if (values.jenkinsUrl && values.jenkinsUsername && values.jenkinsApiToken) {
                return {
                    url: values.jenkinsUrl,
                    username: values.jenkinsUsername,
                    accessToken: values.jenkinsApiToken,
                };
            } else {
                return null;
            }
        case IntegrationType.TravisCI:
            if (values.url && values.accessToken) {
                return {
                    url: values.url,
                    accessToken: values.accessToken,
                };
            } else {
                return null;
            }
        case IntegrationType.TestRail:
            if (values.url && values.username && values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.BrowserStack:
            if (values.username && values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.Qase:
            if (values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.CircleCI:
            if (values.accessToken) {
                return {
                    accessToken: values.accessToken,
                };
            } else {
                return null;
            }
        case IntegrationType.Amazon:
            if (values.clientId && values.clientSecret) {
                return {
                    clientId: values.clientId,
                    clientSecret: values.clientSecret,
                };
            } else {
                return null;
            }
        case IntegrationType.Google:
            if (values.packageName && values.developerId) {
                return {
                    packageName: values.packageName,
                    developerId: values.developerId,
                };
            } else {
                return null;
            }
        case IntegrationType.HuaweiAppGallery:
            if (values.clientId && values.clientSecret) {
                const connectData: HuaweiIntegrationRequestDataType = {
                    clientId: values.clientId,
                    clientSecret: values.clientSecret,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.Samsung:
            if (values.serviceAccountId) {
                const connectData: SamsungIntegrationRequestDataType = {
                    serviceAccountId: values.serviceAccountId,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.GoogleBeta:
            if (values.packageName && values.developerId) {
                return {
                    packageName: values.packageName,
                    developerId: values.developerId,
                };
            } else {
                return null;
            }
        case IntegrationType.FirebaseBeta:
            return {};
        case IntegrationType.Firebase:
            return {};
        case IntegrationType.AndroidVitals:
            return {};
        case IntegrationType.ApplePowerPerformanceMetrics:
            return {};
        case IntegrationType.GoogleAnalytics:
            return values.propertyId ? values : null;
        case IntegrationType.Xray:
            if (values.username && values.password) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.AppCenterCI:
            if (values.accessToken) {
                return {
                    accessToken: values.accessToken,
                };
            } else {
                return null;
            }
        case IntegrationType.AppCenterBeta:
            if (values.accessToken) {
                return {
                    accessToken: values.accessToken,
                };
            } else {
                return null;
            }
        case IntegrationType.Amplitude:
            if (values.apiKey && values.apiSecret) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.Optimizely:
            if (values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.LaunchDarkly:
            if (values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.Statsig:
            if (values.accessToken) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.DatadogAnalytics:
        case IntegrationType.DatadogStabilityMonitoring:
            if (values.apiKey && values.applicationKey && values.datadogDatacenter) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.MixpanelAnalytics:
            if (values.serviceAccountUser && values.serviceAccountSecret) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.RunwayBuildDistroBeta:
            return {};
        case IntegrationType.EmbraceStabilityMonitoring:
            if (values.apiKey) {
                return {
                    apiKey: values.apiKey,
                };
            } else {
                return null;
            }
        case IntegrationType.Crowdin:
            if (values.accessToken) {
                const connectData: CrowdinIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                    subdomain: values.subdomain,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.Lokalise:
            if (values.accessToken) {
                const connectData: LokaliseIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.PagerDutyScheduling:
            if (values.accessToken) {
                const connectData: PagerDutyIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.RootlyScheduling:
            if (values.accessToken) {
                const connectData: RootlyIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.IncidentIoScheduling:
            if (values.accessToken) {
                const connectData: IncidentIoIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                };
                return connectData;
            } else {
                return null;
            }
        case IntegrationType.NewRelicStabilityMonitoring:
        case IntegrationType.NewRelicAnalytics:
            if (values.apiKey) {
                return values;
            } else {
                return null;
            }
        case IntegrationType.OpsgenieScheduling:
            if (values.accessToken) {
                const connectData: OpsgenieIntegrationRequestDataType = {
                    accessToken: values.accessToken,
                    shouldUseEUServer: values.shouldUseEUServer,
                };
                return connectData;
            } else {
                return null;
            }

        case IntegrationType.DynatraceStabilityMonitoring:
            if (values.accessToken && values.environmentId) {
                return values;
            }
            return null;
        case IntegrationType.CustomAnalytics:
            if (values.displayableName) {
                return values;
            }
            return null;
        case IntegrationType.CustomCI:
            if (values.displayableName) {
                return values;
            }
            return null;
        case IntegrationType.GoogleCalendar:
            return values;
        default:
            return null;
    }
};

export const requiresFileUpload = (integrationType: IntegrationType) => {
    switch (integrationType) {
        case IntegrationType.Apple:
        case IntegrationType.Google:
        case IntegrationType.TestFlight:
        case IntegrationType.XcodeCloud:
        case IntegrationType.GoogleBeta:
        case IntegrationType.Firebase:
        case IntegrationType.AndroidVitals:
        case IntegrationType.ApplePowerPerformanceMetrics:
        case IntegrationType.FirebaseBeta:
        case IntegrationType.GoogleAnalytics:
        case IntegrationType.GoogleCalendar:
        case IntegrationType.Samsung:
            return true;
        default:
            return false;
    }
};

export const s3KeyFromFileUpload = (files: UploadFile<unknown>[]) => {
    const uploadedFile = files.find((file) => file.status === 'done');
    const response = uploadedFile?.response as RunwayResponse;
    const s3Key = response?.data?.s3Key;

    return s3Key ?? null;
};

const IncidentIoApiKeyPermissionsInfo: React.FC = () => (
    <div style={{ marginTop: -12, fontSize: 13 }}>
        <Text type="secondary">Runway requires the following permissions:</Text>
        <ul style={{ margin: '4px 0 0 0', paddingLeft: 20, color: 'inherit' }}>
            <li style={{ textDecorationLine: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
                <Tooltip title="Required for synchronizing pilot rotation.">
                    <Text type="secondary" style={{ cursor: 'help' }}>
                        Read schedules
                    </Text>
                </Tooltip>
            </li>
            <li style={{ textDecorationLine: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
                <Tooltip
                    title={`Required by the "Create incident/alert in incident management & scheduling tool" automation.`}
                >
                    <Text type="secondary" style={{ cursor: 'help' }}>
                        Create incidents
                    </Text>
                </Tooltip>
            </li>
        </ul>
    </div>
);

export const warningMessageForIntegrationType: (integrationType: IntegrationType) => string | React.ReactNode = (
    integrationType,
) => {
    switch (integrationType) {
        case IntegrationType.Firebase:
            return (
                <>
                    Runway's {displayNameForIntegrationType(integrationType)} integration relies on Google Analytics
                    data which must be exported via BigQuery. BigQuery data processed, and therefore costs, vary
                    depending on your team's data volume and other factors. We highly recommend adding user- and/or
                    project-level quotas to cap query data processed per day. For more information, please visit our{' '}
                    <Link
                        href="https://docs.runway.team/integrations/stability-monitoring/firebase-crashlytics#bigquery--ga-settings"
                        target="blank"
                    >
                        docs
                    </Link>
                    .
                </>
            );
        default:
            return null;
    }
};
